import { useState, useMemo } from 'react';
import { Tag } from '@carbon/react';
import {
    createColumnHelper,
} from '@tanstack/react-table';
import { defineMessages, useIntl } from 'react-intl';

import styles from './EntityTable.module.scss';

import EntityInteractions from 'Screens/Dashboard/AllEntities/EntityTable/EntityInteractions/EntityInteractions';
import { useGetEntitiesQuery } from 'api/entityEndpoints';
import EntityRole from 'Screens/Dashboard/AllEntities/EntityTable/EntityRole/EntityRole';
import { UserRole } from 'api/dtos/user';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import sharedMessages from 'Components/utils/sharedMessages';
import ConfigurableTanstackTable from 'Components/Common/ConfigurableTanstackTable/ConfigurableTanstackTable';
import TableSearchInput from 'Components/Common/TableSearchInput/TableSearchInput';
import cx from 'classnames';

interface EntityRowData {
    id: string;
    name: string;
    parentId?: string | null;
    parentName?: string;
    isChild: boolean;
    isParish: boolean;
    isIndependent: boolean;
    usesPoints: boolean;
    role: {
        isLastAdmin: boolean;
        role: UserRole;
    };
    interactions: {
        id: string;
        role: UserRole;
    };
    addToken: string;
}

const columnHelper = createColumnHelper<EntityRowData>();

const getColumns = (intl: ReturnType<typeof useIntl>) => [
    columnHelper.accessor('name', {
        id: 'name',
        header: () => intl.formatMessage(messages.name),
        cell: ({ row, getValue }) => {
            const tableRow = row.original;
            return (
                <div className={styles.nameCell}>
                    <div className={styles.nameText}>{getValue()}</div>
                    {tableRow.isParish && (
                        <Tag type="green" size="sm">
                            {intl.formatMessage(messages.parish)}
                        </Tag>
                    )}
                    {tableRow.isIndependent && (
                        <Tag type="purple" size="sm">
                            {intl.formatMessage(messages.independent)}
                        </Tag>
                    )}
                    {!tableRow.isParish && !tableRow.isIndependent && tableRow.isChild && tableRow.parentName && (
                        <Tag type="blue" size="sm">
                            {intl.formatMessage(messages.childOf, { name: tableRow.parentName })}
                        </Tag>
                    )}
                    {!tableRow.isParish && !tableRow.isIndependent && !tableRow.isChild && (
                        <Tag type="gray" size="sm">
                            {intl.formatMessage(messages.mainGroup)}
                        </Tag>
                    )}
                    {!tableRow.usesPoints && (
                        <Tag type="red" size="sm">
                            {intl.formatMessage(messages.noPoints)}
                        </Tag>
                    )}
                </div>
            );
        },
    }),
    columnHelper.display({
        id: 'role',
        header: () => intl.formatMessage(sharedMessages.role),
        cell: ({ row }) => <EntityRole id={row.original.id} role={row.original.role.role} />,
    }),
    columnHelper.accessor('addToken', {
        id: 'addToken',
        header: () => intl.formatMessage(messages.addToken),
        cell: ({ getValue }) => <span className={styles.tokenCell}>{getValue()}</span>,
    }),
    columnHelper.display({
        id: 'interactions',
        header: () => intl.formatMessage(sharedMessages.interactions),
        cell: ({ row }) => <EntityInteractions id={row.original.id} role={row.original.role.role} />,
    }),
];

const EntityTable = () => {
    const { data } = useGetEntitiesQuery();
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const [search, setSearch] = useState('');
    const isEmpty = data?.length === 0;

    const localRows: EntityRowData[] = useMemo(
        () => (data ?? []).map((entity) => ({
            id: entity.id,
            name: entity.name,
            parentId: entity.parentId,
            parentName: undefined,
            isChild: entity.isChild,
            isParish: entity.isParish,
            isIndependent: entity.isIndependent,
            usesPoints: entity.usesPoints,
            role: {
                isLastAdmin: entity.isUserLastAdmin,
                role: entity.role,
            },
            interactions: {
                id: entity.id,
                role: entity.role,
            },
            addToken: entity.invitationToken,
        })),
        [data],
    );

    const orderedRows = useMemo(() => {
        const parents = localRows.filter((r) => !r.isChild);
        const children = localRows.filter((r) => r.isChild);

        const placedChildIds = new Set<string>();

        const grouped = parents.reduce<EntityRowData[]>((acc, parent) => {
            const ownChildren = children
                .filter((c) => c.parentId === parent.id)
                .map((c) => {
                    placedChildIds.add(c.id);
                    return { ...c, parentName: parent.name };
                });
            return [...acc, parent, ...ownChildren];
        }, []);

        const orphans = children.filter((c) => !placedChildIds.has(c.id));

        return [...grouped, ...orphans];
    }, [localRows]);

    const filteredRows = useMemo(() => {
        const term = search.toLowerCase().trim();
        if (!term) return orderedRows;
        return orderedRows.filter((row) =>
            row.name.toLowerCase().includes(term) ||
            row.parentName?.toLowerCase().includes(term));
    }, [search, orderedRows]);

    const hasRows = filteredRows.length > 0;

    const columns = useMemo(() => getColumns(intl), [intl]);

    return (
        <div className={cx(styles.container, isEmpty && styles.emptyContainer)}>
            {hasRows && (
                <TableSearchInput
                    id="search-entity-table"
                    placeholder={intl.formatMessage(messages.searchPlaceholder)}
                    value={search}
                    onChange={setSearch}
                />
            )}
            <ConfigurableTanstackTable
                data={filteredRows}
                columns={columns}
                getRowId={(row) => row.id}
                rowClassName={(row) => (row.original.id === currentEntityId ? styles.activeGroup : undefined)}
                emptyStateTitle={intl.formatMessage(messages.emptyState)}
            />
        </div>
    );
};

const messages = defineMessages({
    name: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityTable.name',
        defaultMessage: 'Nazwa grupy',
    },
    addToken: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityTable.addToken',
        defaultMessage: 'Kod dołączenia',
    },
    searchPlaceholder: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityTable.searchPlaceholder',
        defaultMessage: 'Przeszukaj po nazwie',
    },
    childOf: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityTable.childOf',
        defaultMessage: 'Grupa parafialna: {name}',
    },
    mainGroup: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityTable.mainGroup',
        defaultMessage: 'Grupa główna',
    },
    parish: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityTable.parish',
        defaultMessage: 'Parafia',
    },
    independent: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityTable.independent',
        defaultMessage: 'Grupa niezależna',
    },
    noPoints: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityTable.noPoints',
        defaultMessage: 'Bez punktacji',
    },
    emptyState: {
        id: 'src.Screens.Dashboard.AllEntities.EntityTable.EntityTable.emptyState',
        defaultMessage: 'Brak grup do wyświetlenia.',
    },
});

export default EntityTable;
